import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Quá trình hoá hơi & nhiệt hoá hơi riêng (Lớp 12 — Bài 6):
// Đun nước: nhiệt độ tăng tới 100°C rồi GIỮ NGUYÊN trong khi nước sôi & hoá hơi.
// Nhiệt lượng cấp lúc này dùng để chuyển nước → hơi: Q = L·m (L = nhiệt hoá hơi riêng).
const W = 640, H = 406
const BX0 = 120, BX1 = 300, BTOP = 96, BBOT = 280, WMAXH = 168
const TX = 348           // nhiệt kế
const GX0 = 410, GX1 = 624, GY0 = 104, GY1 = 250   // đồ thị T–Q

const VaporizationSimulation: React.FC<Props> = ({
  title = 'Quá trình hoá hơi (nhiệt hoá hơi riêng)',
  description = 'Đun nước: nhiệt độ tăng tới 100°C rồi giữ nguyên trong lúc sôi và hoá hơi. Nhiệt lượng cấp khi đó dùng để hoá hơi: Q = L·m.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [q, setQ] = useState(0)
  const [running, setRunning] = useState(true)
  const qRef = useRef(q), runRef = useRef(running)
  const firstRef = useRef(false)
  useEffect(() => { qRef.current = q }, [q])
  useEffect(() => { runRef.current = running }, [running])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  // 0–0.3 nóng lên (25→100); 0.3–0.9 sôi đẳng nhiệt; >0.9 nước cạn → nhiệt độ tăng tiếp
  const tempOf = (x: number) => x < 0.3 ? 25 + 75 * (x / 0.3) : x < 0.9 ? 100 : 100 + (x - 0.9) / 0.1 * 40
  const waterFrac = (x: number) => x < 0.3 ? 1 : x < 0.92 ? 1 - ((x - 0.3) / 0.62) * 0.96 : Math.max(0, 0.04 - (x - 0.92) / 0.08 * 0.04)
  const stateOf = (x: number) => x < 0.3 ? 'Nước đang nóng lên' : x < 0.9 ? 'Đang sôi & hoá hơi (100°C)' : x < 0.99 ? 'Nước gần cạn' : 'Nước đã cạn (khô)'

  const roundRectPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath(); ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r); ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y)
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    const t = tRef.current, qq = qRef.current, temp = tempOf(qq), wf = waterFrac(qq), boiling = qq >= 0.3 && qq < 0.92
    // nền
    const bg = ctx.createLinearGradient(0, 0, 0, H); bg.addColorStop(0, '#fbfdff'); bg.addColorStop(1, '#eef3f8')
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)

    // ===== HƠI NƯỚC bay lên =====
    const steamN = Math.round(30 * (1 - wf) + 6)
    for (let i = 0; i < steamN; i++) {
      const ph = i * 1.9
      const sx = BX0 + 24 + ((i * 47) % (BX1 - BX0 - 48)) + Math.sin(t * 1.2 + ph) * 10
      const rise = (t * 24 + i * 38) % (BTOP + 70)
      const sy = (BBOT - wf * WMAXH) - rise
      if (sy < 6) continue
      const a = Math.max(0, 0.42 - rise / (BTOP + 150))
      ctx.fillStyle = `rgba(186,200,214,${a})`
      ctx.beginPath(); ctx.arc(sx, sy, 6 + rise * 0.03, 0, Math.PI * 2); ctx.fill()
    }

    const waterTop = BBOT - wf * WMAXH
    // ===== NƯỚC =====
    const wg = ctx.createLinearGradient(0, waterTop, 0, BBOT); wg.addColorStop(0, '#7dd3fc'); wg.addColorStop(1, '#0284c7')
    ctx.fillStyle = wg
    ctx.beginPath(); ctx.moveTo(BX0 + 3, waterTop)
    for (let x = BX0 + 3; x <= BX1 - 3; x += 6) ctx.lineTo(x, waterTop + Math.sin(x * 0.08 + t * 3) * 2.5)
    ctx.lineTo(BX1 - 3, BBOT - 3); ctx.lineTo(BX0 + 3, BBOT - 3); ctx.closePath(); ctx.fill()
    // phân tử nước
    const jit = 1 + (temp - 25) / 16, cols = 7, rows = 5
    if (wf > 0.05) for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const bx = BX0 + 20 + c * ((BX1 - BX0 - 38) / (cols - 1))
      const by = waterTop + 16 + r * ((BBOT - waterTop - 26) / (rows - 1))
      if (by < waterTop + 8) continue
      const x = bx + Math.sin(t * jit + r * 1.7 + c) * 2.6 * jit
      const y = by + Math.cos(t * jit * 1.1 + c * 1.3) * 2.6 * jit
      ctx.fillStyle = '#0369a1'; ctx.beginPath(); ctx.arc(x, y, 3.6, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.beginPath(); ctx.arc(x - 1, y - 1, 1.2, 0, Math.PI * 2); ctx.fill()
    }
    // bọt khí khi sôi
    if (boiling) for (let i = 0; i < 8; i++) {
      const x = BX0 + 24 + ((i * 61) % (BX1 - BX0 - 44))
      const prog = (t * 0.5 + i * 0.13) % 1
      const y = BBOT - 6 - prog * (BBOT - waterTop - 10)
      ctx.strokeStyle = `rgba(255,255,255,${0.85 - prog * 0.5})`; ctx.lineWidth = 1.4
      ctx.beginPath(); ctx.arc(x, y, 2 + prog * 4, 0, Math.PI * 2); ctx.stroke()
    }

    // ===== CỐC THUỶ TINH =====
    ctx.strokeStyle = 'rgba(148,170,190,0.9)'; ctx.lineWidth = 3.5; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(BX0 - 6, BTOP - 6); ctx.lineTo(BX0, BTOP); ctx.lineTo(BX0, BBOT); ctx.lineTo(BX1, BBOT); ctx.lineTo(BX1, BTOP); ctx.lineTo(BX1 + 6, BTOP - 6); ctx.stroke()
    ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(BX0 + 10, BTOP + 14); ctx.lineTo(BX0 + 10, BBOT - 14); ctx.stroke() // highlight

    // ===== ĐÈN CỒN (3D) + NGỌN LỬA =====
    const cxL = (BX0 + BX1) / 2
    ctx.fillStyle = '#94a3b8'; ctx.fillRect(BX0 - 6, BBOT, BX1 - BX0 + 12, 4) // lưới đỡ
    // bình thuỷ tinh đựng cồn (gradient ngang → khối 3D)
    const lampTop = 336, lampBot = 398, lw = 86
    const lg = ctx.createLinearGradient(cxL - lw / 2, 0, cxL + lw / 2, 0)
    lg.addColorStop(0, '#7c8a9c'); lg.addColorStop(0.45, '#eef2f7'); lg.addColorStop(1, '#7c8a9c')
    ctx.fillStyle = lg; roundRectPath(ctx, cxL - lw / 2, lampTop, lw, lampBot - lampTop, 16); ctx.fill()
    ctx.fillStyle = 'rgba(96,165,250,0.4)'; roundRectPath(ctx, cxL - lw / 2 + 7, lampTop + 18, lw - 14, lampBot - lampTop - 26, 10); ctx.fill() // cồn
    ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.fillRect(cxL - lw / 2 + 9, lampTop + 8, 6, lampBot - lampTop - 18) // highlight
    // cổ + nắp đồng giữ bấc
    ctx.fillStyle = '#b45309'; roundRectPath(ctx, cxL - 18, 320, 36, 18, 5); ctx.fill()
    ctx.fillStyle = '#92400e'; ctx.fillRect(cxL - 20, 334, 40, 4)
    ctx.fillStyle = '#fde68a'; ctx.fillRect(cxL - 3, 306, 6, 16) // bấc
    // ngọn lửa hướng LÊN, 3 lớp (chạm đáy cốc)
    const wickY = 308, flick = Math.sin(t * 9) * 4
    const flame = (hw: number, topY: number, col: string) => { ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(cxL - hw, wickY); ctx.quadraticCurveTo(cxL - hw, topY + 8, cxL, topY); ctx.quadraticCurveTo(cxL + hw, topY + 8, cxL + hw, wickY); ctx.closePath(); ctx.fill() }
    flame(15, BBOT - 2 - flick, 'rgba(251,146,60,0.92)')
    flame(10, BBOT + 6 - flick, 'rgba(251,191,36,0.95)')
    flame(5, BBOT + 16 - flick, 'rgba(191,219,254,0.95)')

    // ===== NHIỆT KẾ =====
    const ty0 = BTOP + 4, ty1 = BBOT - 6
    ctx.fillStyle = '#fff'; ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 2
    roundRectPath(ctx, TX - 7, ty0, 14, ty1 - ty0, 7); ctx.fill(); ctx.stroke()
    ctx.beginPath(); ctx.arc(TX, ty1 + 9, 12, 0, Math.PI * 2); ctx.fillStyle = '#dc2626'; ctx.fill()
    const lev = ty1 - (Math.min(140, temp) / 140) * (ty1 - ty0)
    ctx.fillStyle = '#dc2626'; ctx.fillRect(TX - 4, lev, 8, ty1 - lev)
    // vạch chia
    ctx.strokeStyle = '#94a3b8'; ctx.fillStyle = '#64748b'; ctx.font = '9px sans-serif'; ctx.lineWidth = 1; ctx.textAlign = 'right'
    for (let v = 0; v <= 140; v += 20) { const yy = ty1 - (v / 140) * (ty1 - ty0); ctx.beginPath(); ctx.moveTo(TX + 8, yy); ctx.lineTo(TX + 13, yy); ctx.stroke(); ctx.fillText(String(v), TX + 30, yy + 3) }
    ctx.textAlign = 'left'
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 14px sans-serif'; ctx.fillText(`${temp.toFixed(0)}°C`, TX - 14, ty0 - 8)

    // ===== ĐỒ THỊ T–Q =====
    const X = (x: number) => GX0 + x * (GX1 - GX0)
    const Y = (T0: number) => GY1 - (Math.min(140, T0) / 140) * (GY1 - GY0)
    ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 1
    for (let v = 20; v <= 140; v += 20) { ctx.beginPath(); ctx.moveTo(GX0, Y(v)); ctx.lineTo(GX1, Y(v)); ctx.stroke() }
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 1.8; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(GX0, GY0 - 6); ctx.lineTo(GX0, GY1); ctx.lineTo(GX1, GY1); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(GX0, GY0 - 10); ctx.lineTo(GX0 - 4, GY0 - 2); ctx.lineTo(GX0 + 4, GY0 - 2); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(GX1 + 6, GY1); ctx.lineTo(GX1 - 2, GY1 - 4); ctx.lineTo(GX1 - 2, GY1 + 4); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 11px sans-serif'; ctx.fillText('T (°C)', GX0 - 4, GY0 - 12); ctx.fillText('thời gian (s)', GX1 - 60, GY1 + 16)
    // đường nằm ngang 100°C
    ctx.strokeStyle = 'rgba(220,38,38,0.4)'; ctx.setLineDash([4, 3]); ctx.beginPath(); ctx.moveTo(GX0, Y(100)); ctx.lineTo(GX1, Y(100)); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#dc2626'; ctx.font = '10px sans-serif'; ctx.fillText('100', GX0 - 22, Y(100) + 3)
    // đường cong T(Q)
    // CHỈ vẽ phần đã chạy qua (phía SAU điểm) — không hiện đường cong phía trước
    ctx.strokeStyle = '#ea580c'; ctx.lineWidth = 2.6; ctx.beginPath()
    const iMaxV = Math.max(0, Math.ceil(qq * 100))
    for (let i = 0; i <= iMaxV; i++) { const x = Math.min(qq, i / 100); const px = X(x), py = Y(tempOf(x)); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) }
    ctx.stroke()
    ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(X(qq), Y(temp), 5, 0, Math.PI * 2); ctx.fill()
    // nhãn đoạn hoá hơi
    ctx.fillStyle = '#b45309'; ctx.font = 'bold 10px sans-serif'; ctx.fillText('hoá hơi (Q = L·m)', X(0.45), Y(100) - 6)

    // trạng thái
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 13px sans-serif'; ctx.fillText(stateOf(qq), BX0 - 6, 40)
  }, [])

  const tick = useCallback(() => {
    tRef.current += 0.05
    if (runRef.current) { const nq = Math.min(1, qRef.current + 0.0022); qRef.current = nq; setQ(nq); if (nq >= 1) setRunning(false) }
    draw(); raf.current = requestAnimationFrame(tick)
  }, [draw])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  return (
    <div className="rounded-2xl border border-orange-200 bg-white p-4 my-3">
      <h4 className="font-bold text-orange-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100" />
      <label className="flex items-center gap-2 text-sm mt-3"><span className="w-32 text-gray-600">Thời gian đun</span>
        <input type="range" min={0} max={1} step={0.01} value={q} onChange={(e) => { touch(); setRunning(false); setQ(Number(e.target.value)) }} className="flex-1 accent-orange-500" />
        <span className="w-12 text-right font-semibold">{Math.round(q * 100)}%</span></label>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); if (q >= 1) setQ(0); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500 text-white font-bold hover:bg-orange-600">{running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Đun</>}</button>
        <button onClick={() => { touch(); setQ(0); setRunning(false) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Đoạn nằm ngang trên đồ thị T–Q (ở 100°C): nhiệt độ không đổi dù vẫn cấp nhiệt — nhiệt lượng dùng để hoá hơi. Q = L·m với L là nhiệt hoá hơi riêng.</p>
    </div>
  )
}
export default VaporizationSimulation
